import { indexedDBStore, SyncQueueItem } from '@shared/storage/indexedDb';
import { supabase } from '@/src/supabaseClient';
import { queryClient } from '@shared/query/queryClient';
import { queryKeys } from '@shared/query/queryKeys';
import { eventBus } from '@shared/events/eventBus';
import { toast } from 'sonner';
import { logger } from '@shared/logging/logger';

class SyncQueue {
  private isProcessing = false;

  /**
   * Enqueue a write mutation that was attempted while offline.
   */
  public async enqueue(
    endpoint: string,
    method: SyncQueueItem['method'],
    payload: any,
    meta?: Record<string, any>
  ): Promise<SyncQueueItem> {
    const item: SyncQueueItem = {
      id: `${endpoint}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      endpoint,
      method,
      payload,
      timestamp: new Date().toISOString(),
      retries: 0,
      status: 'pending',
      meta,
    };

    await indexedDBStore.set('sync_queue', item);
    logger.info('sync_queue', 'Enqueued offline mutation', item);
    
    // Trigger event
    eventBus.emit('app:notification', {
      id: item.id,
      title: 'Modo Offline Ativo',
      message: 'Sua atividade foi salva localmente e será sincronizada assim que a internet retornar.',
      type: 'info',
    });

    return item;
  }

  /**
   * Retrieve all items currently in the queue.
   */
  public async getQueue(): Promise<SyncQueueItem[]> {
    return await indexedDBStore.getAll<SyncQueueItem>('sync_queue');
  }

  /**
   * Processes all pending entries in the synchronization queue.
   * Leverages Supabase SDK directly to perform remote db writes safely.
   */
  public async processQueue(): Promise<boolean> {
    if (this.isProcessing) return false;
    this.isProcessing = true;

    const queue = await this.getQueue();
    if (queue.length === 0) {
      this.isProcessing = false;
      return true;
    }

    logger.info('sync_queue', `Processing offline queue (${queue.length} items)...`);
    let successfulSyncsCount = 0;

    // Process items linearly to preserve order (avoiding race conditions)
    for (const item of queue) {
      try {
        item.status = 'syncing';
        await indexedDBStore.set('sync_queue', item);

        await this.syncItem(item);

        // Delete from offline database upon success
        await indexedDBStore.delete('sync_queue', item.id);
        successfulSyncsCount++;

        // Invalidate corresponding caches depending on what was synced
        this.performCacheInvalidation(item);
      } catch (err: any) {
        logger.error('sync_queue', `Sincronização falhou para item ${item.id}`, err);
        
        item.retries++;
        item.status = item.retries > 5 ? 'failed' : 'pending';
        item.error = err?.message || 'Unknown network error';
        
        await indexedDBStore.set('sync_queue', item);
      }
    }

    this.isProcessing = false;

    if (successfulSyncsCount > 0) {
      toast.success(`${successfulSyncsCount} alteração(ões) offline sincronizadas com o servidor!`);
    }

    return true;
  }

  /**
   * Direct synchronization handler calling Supabase API.
   */
  private async syncItem(item: SyncQueueItem): Promise<void> {
    const { endpoint, method, payload } = item;

    // Basic conflict resolution rule:
    // When executing insertions/updates look if records are newer,
    // we match table names against endpoints (e.g. 'descartes' table)
    const tableName = endpoint;

    if (method === 'POST') {
      const { error } = await supabase.from(tableName).insert(payload);
      if (error) throw error;
    } else if (method === 'PUT' || method === 'PATCH') {
      // In updates, resolve conflicts using timestamps (data_atualizacao vs timestamp)
      if (payload.id) {
        // Fetch current device status first for eventual consistency checks
        const { data: serverRecord } = await supabase
          .from(tableName)
          .select('id, data_atualizacao')
          .eq('id', payload.id)
          .maybeSingle();

        if (serverRecord && serverRecord.data_atualizacao) {
          const serverTime = new Date(serverRecord.data_atualizacao).getTime();
          const localTime = new Date(item.timestamp).getTime();

          // Server has newer content, do not overwrite (basic conflict resolution)
          if (serverTime > localTime) {
            logger.warn('sync_queue', `Sync Conflict: Server has newer record for ${tableName}:${payload.id}. Keeping server state.`);
            return; 
          }
        }

        const { error } = await supabase
          .from(tableName)
          .update({
            ...payload,
            data_atualizacao: new Date().toISOString(), // updated_at is data_atualizacao as requested by rules
          })
          .eq('id', payload.id);
        
        if (error) throw error;
      } else {
        throw new Error('Syncing PUT/PATCH requires id property in payload');
      }
    } else if (method === 'DELETE') {
      if (payload.id) {
        const { error } = await supabase.from(tableName).delete().eq('id', payload.id);
        if (error) throw error;
      } else {
        throw new Error('Syncing DELETE requires id property in payload');
      }
    }
  }

  /**
   * Automated selective cache invalidations across our React Query client.
   */
  private performCacheInvalidation(item: SyncQueueItem): void {
    const { endpoint, method, payload, meta } = item;

    if (endpoint === 'descartes') {
      queryClient.invalidateQueries({ queryKey: queryKeys.descartes.all() });
      if (payload?.usuario_id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.descartes.history(payload.usuario_id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.user.profile(payload.usuario_id) });
      }
      if (meta?.userId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.descartes.history(meta.userId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.user.profile(meta.userId) });
      }
    } else if (endpoint === 'ecopontos') {
      queryClient.invalidateQueries({ queryKey: queryKeys.ecopontos.list() });
    }
  }
}

export const syncQueue = new SyncQueue();
export default syncQueue;
